import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { EquipmentStatusBadge } from '@components/atoms/EquipmentStatusBadge';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import { Checkbox } from '@components/atoms/Checkbox';
import { InputCurrency } from '@components/atoms/InputCurrency';
import { EquipmentAsset, EquipmentStatus } from '@models/warehouse.model';
import {
  useEquipmentAssets,
  useCreateTaskEquipmentFromWarehouse,
  useCheckoutTaskEquipment,
} from '@hooks/api/warehouse';
import ConstructionIcon from '@mui/icons-material/Construction';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { toast } from 'react-toastify';

interface EquipmentOption extends SelectOption {
  equipment: EquipmentAsset;
}

interface TaskEquipmentCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  isDelegated?: boolean;
  onSuccess?: () => void;
}

const formatDateDisplay = (date: string | Date) =>
  format(new Date(date), 'MMM dd, yyyy');

export const TaskEquipmentCheckoutModal: React.FC<TaskEquipmentCheckoutModalProps> = ({
  isOpen,
  onClose,
  taskId,
  isDelegated = false,
  onSuccess,
}) => {
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('00:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('23:59');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [chargeToClient, setChargeToClient] = useState(false);
  const [clientPricePerHour, setClientPricePerHour] = useState<number | null>(null);

  const { data: allEquipment = [], isLoading: equipmentLoading } = useEquipmentAssets();
  const createTaskEquipment = useCreateTaskEquipmentFromWarehouse();
  const checkoutTaskEquipment = useCheckoutTaskEquipment();

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setSelectedAssetId('');
      setStartDate('');
      setStartTime('00:00');
      setEndDate('');
      setEndTime('23:59');
      setNotes('');
      setIsSubmitting(false);
      setChargeToClient(false);
      setClientPricePerHour(null);
    }
  }, [isOpen]);

  // Show AVAILABLE and RESERVED (checkout allows both), hide RETIRED
  const selectableEquipment = useMemo(
    () => allEquipment.filter((eq) => eq.status !== EquipmentStatus.RETIRED),
    [allEquipment],
  );

  // Build options: AVAILABLE and RESERVED are selectable, others disabled
  const equipmentOptions: EquipmentOption[] = useMemo(
    () =>
      selectableEquipment.map((eq) => ({
        value: eq.assetId,
        label: `${eq.name}${eq.assetCode ? ` (${eq.assetCode})` : ''}`,
        equipment: eq,
        isDisabled:
          eq.status !== EquipmentStatus.AVAILABLE &&
          eq.status !== EquipmentStatus.RESERVED,
      })),
    [selectableEquipment],
  );

  const selectedEquipment = useMemo(
    () => selectableEquipment.find((eq) => eq.assetId === selectedAssetId),
    [selectableEquipment, selectedAssetId],
  );

  const canSubmit =
    selectedAssetId &&
    (selectedEquipment?.status === EquipmentStatus.AVAILABLE ||
      selectedEquipment?.status === EquipmentStatus.RESERVED) &&
    !isSubmitting;

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  // Custom option renderer with status info
  const formatOptionLabel = (
    option: EquipmentOption,
    { context }: { context: 'menu' | 'value' },
  ) => {
    const eq = option.equipment;
    const isUnavailable =
      eq.status !== EquipmentStatus.AVAILABLE &&
      eq.status !== EquipmentStatus.RESERVED;

    if (context === 'value') {
      return (
        <div className="flex items-center gap-2">
          <ConstructionIcon
            className={isUnavailable ? 'text-gray-400' : 'text-indigo-500'}
            sx={{ fontSize: 16 }}
          />
          <span className="truncate">{eq.name}</span>
          <EquipmentStatusBadge status={eq.status} size="sm" />
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3 w-full py-0.5">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isUnavailable ? 'bg-gray-100' : 'bg-indigo-50'
          }`}>
          <ConstructionIcon
            className={isUnavailable ? 'text-gray-400' : 'text-indigo-500'}
            sx={{ fontSize: 16 }}
          />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span
            className={`text-sm font-medium truncate ${
              isUnavailable ? 'text-gray-400' : 'text-gray-900'
            }`}>
            {eq.name}
          </span>
          <span className="text-xs text-gray-500 truncate">
            {eq.assetCode ? `Code: ${eq.assetCode}` : eq.serialNumber ? `S/N: ${eq.serialNumber}` : ''}
            {eq.status === EquipmentStatus.RESERVED &&
              eq.reservationStartDate &&
              eq.reservationEndDate &&
              ` · Reserved ${formatDateDisplay(eq.reservationStartDate)} — ${formatDateDisplay(eq.reservationEndDate)}`}
          </span>
        </div>
        <div className="flex-shrink-0">
          <EquipmentStatusBadge status={eq.status} size="sm" />
        </div>
      </div>
    );
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      const combinedStartDate = startDate
        ? new Date(`${startDate}T${startTime || '00:00'}:00`).toISOString()
        : undefined;
      const combinedEndDate = endDate
        ? new Date(`${endDate}T${endTime || '23:59'}:00`).toISOString()
        : undefined;

      // Step 1: Create TaskEquipment from warehouse (creates in RESERVED state)
      const taskEquipment = await createTaskEquipment.mutateAsync({
        taskId,
        assetId: selectedAssetId,
        startDate: combinedStartDate,
        endDate: combinedEndDate,
        notes: notes.trim() || undefined,
        costPerHour: selectedEquipment?.hourlyRate || undefined,
        pricePerHour: chargeToClient
          ? (clientPricePerHour ?? selectedEquipment?.clientHourlyRate ?? undefined)
          : selectedEquipment?.clientHourlyRate || undefined,
        chargeToClient,
      });

      // Step 2: Checkout the TaskEquipment (transitions RESERVED → IN_USE)
      await checkoutTaskEquipment.mutateAsync(taskEquipment.taskEquipmentId);

      onSuccess?.();
      onClose();
    } catch (error: any) {
      const message =
        error?.response?.errors?.[0]?.message || 'Failed to checkout equipment';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Info about reserved equipment being checked out
  const reservedInfo =
    selectedEquipment?.status === EquipmentStatus.RESERVED
      ? `This equipment is currently reserved${
          selectedEquipment.reservationStartDate && selectedEquipment.reservationEndDate
            ? ` from ${formatDateDisplay(selectedEquipment.reservationStartDate)} to ${formatDateDisplay(selectedEquipment.reservationEndDate)}`
            : ''
        }. Checking it out will activate the reservation and mark it as "In Use".`
      : null;

  // Rates info for the selected equipment
  const hasRates = selectedEquipment && (
    selectedEquipment.hourlyRate != null || selectedEquipment.clientHourlyRate != null
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Checkout Equipment"
      confirmText="Checkout"
      handleConfirm={handleSubmit}
      isLoading={isSubmitting}
      disableConfirm={!canSubmit}
      size="medium">
      <div className="flex flex-col gap-5">
        {/* Equipment Selection */}
        <FormGroup label="Equipment" htmlFor="equipmentSelect">
          <ReactSelect
            options={equipmentOptions}
            value={
              selectedAssetId
                ? equipmentOptions.find((o) => o.value === selectedAssetId) || null
                : null
            }
            onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
              if (option && !Array.isArray(option) && 'value' in option) {
                setSelectedAssetId(option.value as string);
              } else {
                setSelectedAssetId('');
              }
            }}
            placeholder={equipmentLoading ? 'Loading equipment...' : 'Select equipment...'}
            isSearchable
            isClearable
            isLoading={equipmentLoading}
            formatOptionLabel={formatOptionLabel as any}
          />
        </FormGroup>

        {/* Reserved equipment info */}
        {reservedInfo && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <WarningAmberIcon className="text-amber-500 mt-0.5" sx={{ fontSize: 18 }} />
            <p className="text-sm text-amber-700">{reservedInfo}</p>
          </div>
        )}

        {/* Equipment Details */}
        {selectedEquipment && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <ConstructionIcon className="text-indigo-600" sx={{ fontSize: 20 }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{selectedEquipment.name}</p>
                <p className="text-xs text-gray-500 truncate">
                  {selectedEquipment.serialNumber || selectedEquipment.assetCode || ''}
                  {selectedEquipment.manufacturer ? ` · ${selectedEquipment.manufacturer}` : ''}
                  {selectedEquipment.model ? ` ${selectedEquipment.model}` : ''}
                </p>
              </div>
              <EquipmentStatusBadge status={selectedEquipment.status} size="sm" />
            </div>

            {/* Warehouse location */}
            {selectedEquipment.warehouse && (
              <p className="text-xs text-gray-500 mt-2">
                Location: {selectedEquipment.warehouse.name}
              </p>
            )}

            {/* Rates reference */}
            {hasRates && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  {selectedEquipment.hourlyRate != null && (
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Cost / hr</span>
                      <span className="text-xs font-medium text-gray-900">{formatCurrency(selectedEquipment.hourlyRate)}</span>
                    </div>
                  )}
                  {selectedEquipment.clientHourlyRate != null && (
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Client / hr</span>
                      <span className="text-xs font-medium text-green-700">{formatCurrency(selectedEquipment.clientHourlyRate)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Charge to Client (only for delegated tasks) */}
        {isDelegated && selectedEquipment && (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <p className="text-sm font-medium text-gray-900">Charge to Client</p>
                <p className="text-xs text-gray-500">
                  {chargeToClient
                    ? 'Equipment usage will be billed to the client'
                    : 'Internal cost only — not billed to client'}
                </p>
              </div>
              <Checkbox
                checked={chargeToClient}
                onChange={(checked) => {
                  setChargeToClient(checked);
                  if (checked && clientPricePerHour === null) {
                    setClientPricePerHour(selectedEquipment.clientHourlyRate ?? null);
                  }
                }}
                name="chargeToClient"
              />
            </div>

            {chargeToClient && (
              <FormGroup label="Client Price Per Hour" htmlFor="clientPricePerHour">
                <InputCurrency
                  id="clientPricePerHour"
                  value={clientPricePerHour}
                  onChange={(val) => setClientPricePerHour(val)}
                  placeholder="0.00"
                />
                {selectedEquipment.clientHourlyRate != null &&
                  clientPricePerHour !== selectedEquipment.clientHourlyRate && (
                    <p className="text-xs text-gray-400 mt-1">
                      Default: {formatCurrency(selectedEquipment.clientHourlyRate)}/hr
                    </p>
                  )}
              </FormGroup>
            )}
          </div>
        )}

        {/* Date/Time Fields */}
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="From Date" htmlFor="startDate">
            <Input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="From Time" htmlFor="startTime">
            <Input
              id="startTime"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Until Date" htmlFor="endDate">
            <Input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Until Time" htmlFor="endTime">
            <Input
              id="endTime"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </FormGroup>
        </div>

        {/* Notes */}
        <FormGroup label="Notes" htmlFor="checkoutNotes">
          <textarea
            id="checkoutNotes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            rows={3}
          />
        </FormGroup>

        {/* Empty state */}
        {!equipmentLoading && selectableEquipment.length === 0 && (
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-center">
            <p className="text-sm text-amber-700">
              No equipment registered yet. Add equipment from the Equipment Assets page first.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TaskEquipmentCheckoutModal;
